import * as React from "react";

type IslandPrefs = {
  visibility: Record<string, boolean>;
  titles: Record<string, string>;
};

const DEFAULT_STORAGE_KEY = "islandPrefs_v1";

function readPrefs(storageKey: string): IslandPrefs {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { visibility: {}, titles: {} };
    const parsed = JSON.parse(raw);
    return {
      visibility: parsed.visibility ?? {},
      titles: parsed.titles ?? {},
    };
  } catch {
    return { visibility: {}, titles: {} };
  }
}

export function useIslandPreferences(storageKey = DEFAULT_STORAGE_KEY) {
  const [prefs, setPrefs] = React.useState<IslandPrefs>(() => readPrefs(storageKey));

  // ✅ IMPORTANT: when storageKey changes (user switches), reload from that key
  React.useEffect(() => {
    setPrefs(readPrefs(storageKey));
  }, [storageKey]);

  // persist
  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(prefs));
    } catch {
      // ignore quota / private mode errors
    }
  }, [prefs, storageKey]);

  const setVisibility = (key: string, value: boolean) =>
    setPrefs((p) => ({
      ...p,
      visibility: { ...p.visibility, [key]: value },
    }));

  const setTitle = (key: string, title: string) =>
    setPrefs((p) => ({
      ...p,
      titles: { ...p.titles, [key]: title },
    }));

  return {
    visibility: prefs.visibility,
    titles: prefs.titles,
    setVisibility,
    setTitle,
  };
}
